<!--
Images are sent to backend in 2 parts, as separated 2 form data properties defined by config.
First property has key of the image fields key and contains array of the new uploaded images as Files.
Second property has key defined under key 'deletedArrayKey' and its contains ids of deleted images.

This logic for sending images in 2 properties is for better images tracking, to better know which images are removed and/or which images are newly uploaded.
-->

<template>
  <!--Image stack preview with managing-->
  <div
    class="image-preview-container"
  >
    <span
      v-if="config.label"
      class="label mb-2"
      :title="config.label"
    >
      {{ config.label }}
    </span>
    <div class="image-stack">
      <div
        v-for="(image, index) in imagesToDisplay"
        :key="index"
        style="border: 1px solid #E5E7EB; border-radius: 16px; margin-right: 10px; padding: 5px;"
      >
        <v-badge
          v-if="edit || createMode"
          style="cursor: pointer;"
          overlap
          :offset-x="config.badgeConfig && config.badgeConfig.offsetX ? config.badgeConfig.offsetX : 5"
          :offset-y="config.badgeConfig && config.badgeConfig.offsetY ? config.badgeConfig.offsetY : 7"
          :color="config.badgeConfig && config.badgeConfig.color ? config.badgeConfig.color : '#3B82F6'"
        >
          <!--          X badge icon button for image remove-->
          <template
            #badge
          >
            <v-icon
              style="width: 20px; height: 20px;"
              @click="removeImage(image, index)"
            >
              mdi-close
            </v-icon>
          </template>

          <!--          Image-->
          <v-img
            :src="getImageUrl(image)"
            :class="{ 'main-image': index === 0, 'sub-image': index > 0 }"
            contain
            @click="onShowImagesPreviewDialog(config)"
          />
        </v-badge>

        <!--        Image-->
        <v-img
          v-else
          :src="getImageUrl(image)"
          :class="{ 'main-image': index === 0, 'sub-image': index > 0 }"
          contain
          @click="onShowImagesPreviewDialog(config)"
        />
      </div>
      <div
        v-if="canShowAddButton()"
        style="line-height: 40px;"
      >
        <universal-button
          type="base"
          small
          prepend-icon="mdi-plus"
          :prepend-icon-style="{ width: '40px !important', height: '40px !important', minWidth: 'unset', minHeight: 'unset', margin: 0, color: '#FFFFFF !important' }"
          :disabled="!edit && !createMode"
          color="#65AD45"
          @click="addImage"
        />
      </div>
    </div>
    <input
      ref="imageInput"
      type="file"
      style="display: none"
      multiple
      :accept="'image/*'"
      enctype="multipart/form-data"
      @change="handleFileUpload($event, config)"
    >
    <images-preview-dialog
      v-if="showFilesPreviewConfig.show"
      :show-dialog="showFilesPreviewConfig.show"
      :images="showFilesPreviewConfig.files"
      :dialog-title="showFilesPreviewConfig.title"
      :enable-actions="enableActions"
      @close="showFilesPreviewConfig.show = false"
      @removed-image="event => removeImage(event.file, event.index)"
    />
  </div>
</template>

<script>
import imagesPreviewDialog from '@/global/components/dialogs/ImagesPreviewDialog.vue'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import { forEach, isObject } from 'lodash'
// `this.$store`, not an import of the store - see AutocompleteField.vue for the
// import cycle a direct store import puts a field component into.

// 5 MB
const DEFAULT_IMAGE_SIZE = 10 * 1024 * 1024
export default {
  name: 'ImageStackField',

  components: {
    UniversalButton,
    imagesPreviewDialog
  },

  props: {
    edit: {
      type: Boolean,
      default: false
    },
    createMode: {
      type: Boolean,
      default: false
    },
    enableActions: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: Array,
      default: () => ([])
    },
    data: {
      type: Object,
      default: () => ({})
    },
    updateData: {
      type: Function,
      default: () => {}
    }
  },

  emits: [
    'images:uploaded'
  ],

  data () {
    return {
      showFilesPreviewConfig: {
        show: false,
        files: null,
        title: ''
      },
      imagesToDisplay: [],
      imageIds: [],
      uploadedImages: [],
      imagesToDelete: [],
      dataCopy: {}
    }
  },

  watch: {
    value: {
      immediate: true,
      deep: true,
      handler (images) {
        if (images && images.length && Array.isArray(images) && images.every(image => !(image instanceof File)) && !this.imagesToDisplay.length) {
          for (const imageIndex in images) {
            const image = images[imageIndex]
            if (isObject(image) && image.url && !this.imagesToDisplay.includes(image.url)) {
              this.imagesToDisplay.push(image)
              if (image.id && !this.imageIds.includes(image.id)) {
                this.imageIds.push(image.id)
              }
            }
          }
          if ((images.length && images.every(image => image.url)) || !images.length) {
            this.updateData({
              [this.config.key]: []
            })
          }
        }
        else if (!this.edit && images && images.length && Array.isArray(images) && images.every(image => !(image instanceof File))) {
          this.resetImages()
        }
      }
    },
    data: {
      immediate: true,
      deep: true,
      handler (data) {
        this.dataCopy = data
      }
    },
    uploadedImages: {
      immediate: true,
      deep: true,
      handler (val) {
        if (val && val.length) {
          const imagesToSend = val.map(image => image.value || image)
          this.$emit('images:uploaded', imagesToSend)
        }
      }
    }
  },

  methods: {
    canShowAddButton () {
      if (!this.config.maxImages) {
        return true
      }

      return this.config.maxImages > this.imagesToDisplay.length
    },

    resetImages () {
      this.imagesToDisplay = []
      this.uploadedImages = []
      this.imageIds = []

      for (const imageIndex in this.value) {
        const image = this.value[imageIndex]
        if (image && image.url) {
          this.imagesToDisplay.push(image)
          if (image.id) {
            this.imageIds.push(image.id)
          }
        }
      }

      if ((this.value.length && this.value.every(image => image.url)) || !this.value.length) {
        this.updateData({
          [this.config.key]: [],
          [this.config.deletedArrayKey]: []
        })
      }
    },

    getImageUrl (image) {
      if (image && image.url) {
        return image.url
      }

      return image && typeof image === 'string' ? image : undefined
    },

    onShowImagesPreviewDialog () {
      const { title } = this.config?.imagesPreviewConfig || undefined
      let titleValue = title

      if (typeof title === 'function') {
        titleValue = title(this.dataCopy, this)
      }

      this.$set(this.showFilesPreviewConfig, 'files', this.imagesToDisplay)
      this.$set(this.showFilesPreviewConfig, 'title', titleValue || '')
      this.$set(this.showFilesPreviewConfig, 'show', true)
    },

    removeImage (image, index) {
      this.imagesToDisplay.splice(index, 1)

      const imageId = image?.id || undefined

      if (!this.dataCopy[this.config.deletedArrayKey]) {
        this.dataCopy[this.config.deletedArrayKey] = []
      }

      // Detect if an image that should be deleted came from API (Has id property defined). If so, add its id to deleted ids array
      if (this.config.deletedArrayKey && imageId !== undefined && !this.dataCopy[this.config.deletedArrayKey].includes(imageId)) {
        this.dataCopy[this.config.deletedArrayKey].push(imageId)
      }
      // Otherwise, remove new uploaded image
      // New uploaded image is a base64 encoded string because v-img vuetify component for displaying images does not support File objects
      else if (imageId === undefined && typeof image === 'string') {
        const newImages = this.uploadedImages
          .filter(uploadedImage => uploadedImage && ((uploadedImage.key && uploadedImage.key !== image) || uploadedImage instanceof File))
          .map(uploadedImage => uploadedImage.value || uploadedImage)

        this.updateData({
          [this.config.key]: newImages.every(image => image instanceof File) ? newImages : []
        })

        this.uploadedImages = this.uploadedImages.filter(uploadedImage => uploadedImage && ((uploadedImage.key && uploadedImage.key !== image) || uploadedImage instanceof File))
      }
    },

    addImage () {
      this.$refs.imageInput.click()
      this.$refs.imageInput.value = ''
    },

    handleFileUpload (event) {
      const uploadedImages = event.target.files
      const imageSize = this.config?.maxFileSize || DEFAULT_IMAGE_SIZE

      if (this.config.maxImages && (uploadedImages.length > this.config.maxImages || (uploadedImages.length + this.imagesToDisplay.length) > this.config.maxImages)) {
        this.$store.dispatch('base/notifications/push', this.$t('fleet/vehicle.maximum_images_uploaded').replace(':max-images', this.config.maxImages))
        return
      }

      forEach(uploadedImages, image => {
        // Check file size
        if (image.size > imageSize) {
          this.$store.dispatch('base/notifications/push', this.$t('base.file_size_exceeded')
            .replace(':file-name', image.name)
            .replace(':max-size', (imageSize / (1024 * 1024)).toFixed(2)) + 'MB'
          )
          return
        }
        if (image instanceof File) {
          const reader = new FileReader()
          reader.onload = (e) => {
            this.imagesToDisplay.push(e.target.result)
            this.uploadedImages.push({
              key: e.target.result,
              value: image
            })
          }
          reader.readAsDataURL(image)
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.image-preview-container {
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
}

.image-stack {
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
}

.main-image {
  width: 60px;
  height: 50px;
  cursor: grab;
}

.sub-image {
  width: 50px;
  height: 40px;
  cursor: grab;
}

.x-button-big-image {
  position: absolute;
  top: -0.4rem;
  right: 0.1rem;
  color: white;
  height: 20px;
  width: 20px;
}

.x-button-small-image {
  position: absolute;
  top: -0.4rem;
  right: 0.1rem;
  color: white;
  height: 20px;
  width: 20px;
}

@media (max-width: 600px) {
  .image-stack {
    flex-direction: column;
    align-items: flex-start;
  }

  .image-preview-container {
    margin-bottom: 10px;
  }

  .sub-image {
    margin-top: 18px;
  }

  .x-button-big-image {
    top: -1rem;
  }

  .x-button-small-image {
    top: 0.1rem;
  }

  .preview-button {
    margin-left: 0 !important;
    margin-top: 18px;
  }
}

.label {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #111827;
  line-height: 20px;
  font-weight: 500;
  /* Never wrap - see TextField. `display: block` because `text-overflow` needs a
     block container, and the full text stays readable through `title` on hover. */
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
::v-deep.v-badge__badge {
  padding: unset !important;
}

::v-deep {
  .v-badge__badge {
    padding: unset !important;
  }
}
</style>
